import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SettingService } from 'src/app/system-common/services/setting.service';
import * as Chart from 'chart.js';
import { DashboardService } from '../../services/dashboard.service';

@Component({
  selector: '',
  template: '',
})
export class PartnerWiseChartHandler {
  chart: Chart;
  @Output() closeChart = new EventEmitter<string>();
  pieColorArray = [];
  @Input() usedFor;
  frequencyArr = [
    this.settingService.getLabelValue('monthly'),
    this.settingService.getLabelValue('quarterly'),
    this.settingService.getLabelValue('yearly'),
  ];
  frequency = this.settingService.getLabelValue('yearly');
  selectedCalculationYears: any = [];
  selectedFrequencyTemp: string;
  public calculationyears;
  private originalRectangleDraw;
  private userLoginData;

  // Partner Chanrt
  chartConfigs: { channel: string; year: string }[] = [];
  createdChartIds = new Set<string>();
  chartConfigsWithData: any[] = [];
  public selectedPartnerChannel: string = '';
  public agentPartnerWiseGraph: any;
  public selectedPartner;
  patnersData;
  public partnerChannels = [];
  public channelWiseYears;
  public partnerIds = [];
  multiLevelLabelPlugin: any;
  constructor(public service: DashboardService, public settingService: SettingService) {
    const dateStr = this.service.getDateFormatted(this.settingService.getSessionStorageValue('BatchDate'));
    const year = parseInt(dateStr.split('/')[2], 10);
    this.calculationyears = [String(year), String(year + 1)];
    this.originalRectangleDraw = Chart.elements.Rectangle.prototype.draw;

    if (sessionStorage.getItem('userLoginData') != undefined) {
      this.userLoginData = JSON.parse(sessionStorage.getItem('userLoginData'));
    }
  }

  ngOnInit() {
    this.multiLevelLabelPlugin = this.createMultiLevelLabelPlugin(this.frequency);
    this.loadBarChartDetails();
  }

  public loadBarChartDetails() {
    this.pieColorArray = this.service.getColor();
    setTimeout(() => {
      this.partnerWisePieChartBrowser();
    }, 10);
  }

  partnerWisePieChartBrowser(
    frequency?: string,
    selectedPartnerChannel?: string | string[],
    selectedPartner?: string | string[],
    selectedDateRange?: any
  ) {
    this.initializePartnerPieChart(frequency, selectedPartner);

    this.service.getNewBusinessData(['channelcd', 'parentagencyid'], this.frequency, selectedDateRange).subscribe(
      (responseData) => {
        this.patnersData = this.extractPartnerData(responseData);
        if (!this.patnersData) return;

        const allChannels = Object.keys(this.patnersData);
        this.partnerChannels = ['All', ...allChannels];

        const channelsToProcess = this.getChannelsToProcess(selectedPartnerChannel, allChannels);
        this.partnerIds = this.getUniquePartners(channelsToProcess);

        const preparedCharts = this.buildPartnerCharts(channelsToProcess, selectedDateRange, this.frequency);

        this.chartConfigsWithData = preparedCharts;
      },
      (error) => {
        console.error('Error:', error);
        this.service.handleError(error);
      }
    );
  }

  // Initialize or reset chart variables
  private initializePartnerPieChart(frequency?: string, selectedPartner?: string | string[]) {
    this.patnersData = {};
    this.frequency = frequency || this.frequency || 'QUARTERLY';
    this.selectedPartner = selectedPartner;

    if (this.agentPartnerWiseGraph) {
      this.agentPartnerWiseGraph.forEach((chart) => chart.destroy());
    }

    this.agentPartnerWiseGraph = [];
    this.createdChartIds.clear();
    this.chartConfigs = [];
    this.chartConfigsWithData = [];
  }

  // Extract partner data safely
  private extractPartnerData(responseData: any): any {
    try {
      const parsed = JSON.parse(JSON.stringify(responseData));
      return parsed?.data?.data?.[0] || {};
    } catch (error) {
      console.error('Data parsing error', error);
      return null;
    }
  }

  // Determine which channels to process
  private getChannelsToProcess(
    selectedPartnerChannel: string | string[] | undefined,
    allChannels: string[]
  ): string[] {
    if (
      !selectedPartnerChannel ||
      (Array.isArray(selectedPartnerChannel) && selectedPartnerChannel.includes('All')) ||
      selectedPartnerChannel.length === 0
    ) {
      return allChannels;
    }
    return Array.isArray(selectedPartnerChannel) ? selectedPartnerChannel : [selectedPartnerChannel];
  }

  // Get unique partner IDs across channels
  private getUniquePartners(channels: string[]): string[] {
    const uniquePartners = new Set<string>();
    channels.forEach((channel) => {
      const channelData = this.patnersData[channel];
      if (channelData) {
        Object.keys(channelData).forEach((pid) => uniquePartners.add(pid));
      }
    });
    return [...Array.from(uniquePartners)];
  }

  private buildPartnerCharts(channels: string[], selectedDateRange: any, frequency): any[] {
    const combinedData = this.buildCombinedPartnerChart(channels, selectedDateRange);
    const commonFontSettings = {
      fontFamily: 'montserratregular, Arial, Helvetica, sans-serif',
      fontColor: '#000000',
    };

    const chartData = {
      labels: combinedData.labels,
      datasets: combinedData.datasets,
    };

    const chartOptions = {
      responsive: true,
      title: {
        display: false,
        ...commonFontSettings,
        fontSize: 14,
      },
      tooltips: { mode: 'nearest', intersect: true, bodyFontFamily: commonFontSettings.fontFamily },
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          fontSize: 12,
          ...commonFontSettings,
        },
      },
      plugins: {
        datalabels: false,
      },
      layout: {
        padding: { top: 40 },
      },
      scales: {
        xAxes: [
          {
            stacked: true,
            ticks: {
              callback: function (_, index) {
                const labels: any[] = this.chart.data.labels;
                const period = labels[index][2];
                return period !== 'YEARLY' ? period : '';
              },
              ...commonFontSettings,
            },
            barPercentage: 0.4,
            categoryPercentage: 0.8,
          },
        ],
        yAxes: [
          {
            stacked: true,
            ticks: {
              beginAtZero: true,
              ...commonFontSettings,
            },
          },
        ],
      },
    };
    return [
      {
        chartId: `partnerChart_combined`,
        doughnutChartData: chartData,
        doughnutChartOptions: chartOptions,
        isMultiSeries: chartData.datasets.length > 1,
        doughnutChartTitle: 'Partner Data by Channel and Year',
        chartPlugins: [this.createMultiLevelLabelPlugin(frequency)],
      },
    ];
  }

  /**
   * Custom plugin for multi-level labels (Channel on top, Year below)
   */
  private createMultiLevelLabelPlugin(frequency) {
    return {
      id: 'multiLevelLabel',
      afterDraw: (chart) => {
        const ctx = chart.ctx;
        const xAxis = chart.scales['x-axis-0'];
        if (!xAxis) return;

        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.font = '12px Montserrat, Arial';

        const labels: any[] = chart.data.labels as any[]; // [channel, year, period]
        const ticks = xAxis.ticks;
        const tickSpacing = (xAxis.right - xAxis.left) / ticks.length;

        // --- Bottom row: Quarters (only for quarterly frequency) ---
        if (frequency !== 'YEARLY') {
          ticks.forEach((_, i) => {
            const [channel, year, quarter] = labels[i];
            const x = xAxis.left + i * tickSpacing + tickSpacing / 2;
            const y = chart.chartArea.bottom + 5;
            ctx.fillText(quarter, x, y);
          });
        }

        // --- Middle row: Years ---
        const yearGroups: { [key: string]: { start: number; end: number } } = {};
        labels.forEach(([channel, year], i) => {
          const key = channel + '_' + year;
          if (!yearGroups[key]) {
            yearGroups[key] = { start: i, end: i };
          } else {
            yearGroups[key].end = i;
          }
        });

        Object.keys(yearGroups).forEach((key) => {
          const group = yearGroups[key];
          const startX = xAxis.left + group.start * tickSpacing;
          const endX = xAxis.left + (group.end + 1) * tickSpacing;
          const midX = (startX + endX) / 2;
          const y = chart.chartArea.top - 15;
          ctx.fillText(key.split('_')[1], midX, y);

          // optional dotted line
          ctx.strokeStyle = '#aaa';
          ctx.setLineDash([3, 3]);
          ctx.beginPath();
          ctx.moveTo(endX, chart.chartArea.top);
          ctx.lineTo(endX, chart.chartArea.bottom);
          ctx.stroke();
          ctx.setLineDash([]);
        });

        // --- Top row: Channels ---
        const channelGroups: { [key: string]: { start: number; end: number } } = {};
        labels.forEach(([channel], i) => {
          if (!channelGroups[channel]) {
            channelGroups[channel] = { start: i, end: i };
          } else {
            channelGroups[channel].end = i;
          }
        });

        Object.keys(channelGroups).forEach((channel) => {
          const group = channelGroups[channel];
          const startX = xAxis.left + group.start * tickSpacing;
          const endX = xAxis.left + (group.end + 1) * tickSpacing;
          const midX = (startX + endX) / 2;
          const y = chart.chartArea.top - 35;
          ctx.fillText(channel, midX, y);

          ctx.strokeStyle = '#444';
          ctx.setLineDash([5, 3]);
          ctx.beginPath();
          ctx.moveTo(endX, chart.chartArea.top);
          ctx.lineTo(endX, chart.chartArea.bottom);
          ctx.stroke();
          ctx.setLineDash([]);
        });

        ctx.restore();
      },
    };
  }

  private buildCombinedPartnerChart(channels: string[], selectedDateRange: any) {
    const labels: any[] = [];
    const datasets: any[] = [];
    const backgroundColors = this.service.getColor();

    let partnerIds = this.getUniquePartners(channels);
    if (this.selectedPartner && this.selectedPartner.length > 0 && !this.selectedPartner.includes('All')) {
      partnerIds = partnerIds.filter((pid) => this.selectedPartner.includes(pid));
    }
    const datasetMap: { [pid: string]: number[] } = {};
    partnerIds.forEach((pid) => (datasetMap[pid] = []));

    channels.forEach((channel) => {
      const channelData = this.patnersData[channel];
      if (!channelData) return;

      const years = this.getValidYears(channelData, partnerIds, selectedDateRange);

      years.forEach((year) => {
        // collect all "sub-periods" dynamically (quarters or yearly)
        const allPeriods = new Set<string>();

        partnerIds.forEach((pid) => {
          const yearData = channelData[pid]?.[year] || {};
          if (yearData.data) {
            Object.keys(yearData.data).forEach((p) => allPeriods.add(p)); // quarterly
          } else {
            Object.keys(yearData).forEach((p) => allPeriods.add(p)); // yearly
          }
        });

        const periods = Array.from(allPeriods).sort();

        periods.forEach((period) => {
          labels.push([channel, year, period]);

          partnerIds.forEach((pid) => {
            const yearData = channelData[pid]?.[year] || {};
            let value = 0;

            if (yearData.data && yearData.data[period] !== undefined) {
              value = Number(yearData.data[period]);
            } else if (yearData[period] !== undefined) {
              value = Number(yearData[period]);
            }

            datasetMap[pid].push(value);
          });
        });
      });
    });

    // convert datasetMap to Chart.js datasets
    let colorIndex = 0;
    for (const pid of partnerIds) {
      datasets.push({
        label: pid,
        data: datasetMap[pid],
        backgroundColor: backgroundColors[colorIndex % backgroundColors.length],
        borderColor: backgroundColors[colorIndex % backgroundColors.length],
        tension: 0.3,
        pointStyle: 'circle',
        pointRadius: 6,
        pointHoverRadius: 10,
      });
      colorIndex++;
    }

    return { labels, datasets };
  }

  private getSelectedPartners(channelData: any): string[] {
    const allPartnerIds = Object.keys(channelData);

    if (
      !this.selectedPartner ||
      this.selectedPartner === 'All' ||
      (Array.isArray(this.selectedPartner) &&
        (this.selectedPartner.includes('All') || this.selectedPartner.length === 0))
    ) {
      return allPartnerIds;
    }

    return Array.isArray(this.selectedPartner)
      ? this.selectedPartner.filter((pid) => allPartnerIds.includes(pid))
      : allPartnerIds.includes(this.selectedPartner)
      ? [this.selectedPartner]
      : [];
  }

  // Filter valid years with optional date range
  private getValidYears(channelData: any, partnerIds: string[], selectedDateRange: any): string[] {
    const yearSet = new Set<string>();

    partnerIds.forEach((pid) => {
      Object.keys(channelData[pid] || {}).forEach((year) => yearSet.add(year));
    });

    let allYears = Array.from(yearSet)
      .sort((a, b) => +b - +a)
      .slice(0, 3);

    if (selectedDateRange && selectedDateRange.length > 0) {
      allYears = allYears.filter((year) => selectedDateRange.includes(+year));
    }

    return allYears;
  }

  // Check if any data point is greater than zero
  private hasValidData(channelData: any, partnerIds: string[], year: string): boolean {
    return partnerIds.some((pid) => {
      const yearData = channelData[pid]?.[year]?.data || channelData[pid]?.[year] || {};
      return Object.values(yearData).some((val) => Number(val) > 0);
    });
  }

  /* Filter Code For Partner Chart starts Here */
  onFilterPartnerData() {
    $('#agentPartnerWiseStatusModal').toggle();
  }

  closePartnerModal() {
    $('#agentPartnerWiseStatusModal').hide();
  }

  onChannelChange() {
    const selectedChannel = this.selectedPartnerChannel;
    const channelData = this.patnersData?.[selectedChannel];
    this.partnerIds = channelData ? ['All', ...Object.keys(channelData)] : [];
  }

  // onDateRangePartnerChange(event) {
  //   this.selectedCalculationYears = this.service.onDateRangeChange(event);
  //   this.partnerWisePieChartBrowser(
  //     this.selectedFrequencyTemp,
  //     undefined,
  //     this.selectedPartner,
  //     this.selectedCalculationYears
  //   );
  // }

  onDateRangePartnerChange(event) {
    const selectedYears: string[] = this.service.onDateRangeChange(event);
    if (!selectedYears || selectedYears.length === 0) return;

    this.selectedCalculationYears = selectedYears.map((y) => +y);
    console.log('   this.selectedPartner', this.selectedPartner);

    this.partnerWisePieChartBrowser(
      this.selectedFrequencyTemp,
      this.selectedPartnerChannel,
      this.selectedPartner,
      this.selectedCalculationYears
    );
  }

  filterPartner() {
    $('#agentPartnerWiseStatusModal').hide();

    console.log('   this.selectedPartner', this.selectedPartner);
    this.partnerWisePieChartBrowser(
      this.selectedFrequencyTemp,
      this.selectedPartnerChannel,
      this.selectedPartner,
      this.selectedCalculationYears
    );
  }
  /* Filter Code For Partner Chart Ends Here */

  // Close the Chart on click of the close button
  onCloseClick() {
    this.closeChart.emit(this.chart);
  }

  clearData() {
    this.selectedPartnerChannel = '';
    this.selectedPartner = [];
    this.selectedFrequencyTemp = '';
    this.selectedCalculationYears = [];

    this.frequency = this.settingService.getLabelValue('yearly');

    const datePicker: any = document.getElementById('mydaterange-picker12');
    if (datePicker) {
      datePicker.value = '';
    }

    $('#agentPartnerWiseStatusModal').hide();

    setTimeout(() => {
      if (this.agentPartnerWiseGraph && this.agentPartnerWiseGraph.length) {
        this.agentPartnerWiseGraph.forEach((chart) => chart.destroy());
        this.agentPartnerWiseGraph = [];
      }

      this.chartConfigs = [];
      this.chartConfigsWithData = [];
      this.createdChartIds.clear();

      setTimeout(() => {
        this.partnerWisePieChartBrowser(
          this.frequency,
          this.selectedPartnerChannel,
          this.selectedPartner,
          this.selectedCalculationYears
        );
      }, 10);
    }, 10);
  }

  ngOnDestroy() {
    this.service.clear();
    Chart.elements.Rectangle.prototype.draw = this.originalRectangleDraw;
  }
}
